/**
 * What the major job delete confirmation says and when it lets the operator
 * through. A whole automation used to go on one click of a button that sits
 * next to the task actions, with "nested task jobs will be removed" as the only
 * warning - so the dialog now counts what goes and asks for the name.
 */
import type { MajorJobDeletePreview } from "features/adminUsers/adminUsers";

/** The phrase the bulk delete asks for (a single delete asks for the name). */
export const BULK_DELETE_PHRASE = "DELETE";

/** Typed exactly, surrounding spaces aside. An empty phrase never matches. */
export function confirmPhraseMatches(typed: string, phrase: string): boolean {
  const wanted = phrase.trim();
  return wanted !== "" && typed.trim() === wanted;
}

function counted(n: number, one: string, many: string): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

/** A shared roster file outlives a deleted job only while another job reads it. */
export function rosterKeptNotice(
  jobIds: number[] | null | undefined,
): string | null {
  if (!jobIds?.length) return null;
  const jobs = jobIds.map((id) => `#${id}`).join(", ");
  return jobIds.length === 1
    ? `The roster file stays: job ${jobs} reads it too.`
    : `The roster file stays: jobs ${jobs} read it too.`;
}

/** What deleting the major job removes. */
export function majorJobDeleteSummary(preview: MajorJobDeletePreview): string {
  const rows =
    `${counted(preview.task_jobs, "task job", "task jobs")} with ` +
    counted(preview.tasks, "task", "tasks");
  const kept = rosterKeptNotice(preview.roster_kept_for_job_ids);
  const withKept = (text: string) => (kept ? `${text} ${kept}` : text);
  if (preview.log_archives == null || preview.other_files == null) {
    return (
      `This removes ${rows}. Their S3 data could not be counted just now; ` +
      "it is deleted with them all the same."
    );
  }
  if (preview.log_archives === 0 && preview.other_files === 0) {
    return withKept(
      `This removes ${rows}. None of them has anything in S3 yet.`,
    );
  }
  return withKept(
    `This removes ${rows}, and deletes from S3 ` +
      `${counted(preview.log_archives, "task log archive", "task log archives")} and ` +
      `${counted(preview.other_files, "other file", "other files")} of theirs ` +
      "(rosters, matches files, error screenshots).",
  );
}
